import { TooltipV2, Dropdown, Checkbox } from '@cimplico/components';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { UserOverview } from '../../types/user';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import UserAvatar from '../avatar/UserAvatar';

type ThreadRowProps = {
    id: string;
    checked?: boolean;
    disableCheckbox?: boolean;
    showCheckbox?: boolean;
    onSelect?: (id: string) => void;
    onChecked?: (id: string, title: string, context: string) => void;
    context?: { label: string; url: string };
    createdBy: UserOverview;
    title: string;
    replies: number;
    statusBadge: ReactNode;
    createdAt: Date;
    actions?: { label: string; value: string; hidden: boolean }[];
    onActionsClick?: (value: string) => void;
    disableAllActions?: boolean;
};

function ThreadRow({
    id,
    checked,
    disableCheckbox,
    showCheckbox,
    onSelect,
    onChecked,
    context,
    createdBy,
    title,
    replies,
    statusBadge,
    createdAt,
    onActionsClick,
    actions = [],
    disableAllActions = false,
}: ThreadRowProps): JSX.Element {
    return (
        <div className="flex border-b gap-1">
            {showCheckbox && (
                <Checkbox
                    checked={checked}
                    disabled={disableCheckbox}
                    onChange={(): void => onChecked && onChecked(id, title, context ? context.label : '')}
                />
            )}
            <div
                className="grow p-2 grid hover:bg-gray-50 cursor-pointer"
                style={{ gridTemplateColumns: '3fr 1fr' }}
                onClick={(): void => onSelect && onSelect(id)}
            >
                <div className="flex-col ">
                    {/* CONTEXT */}
                    <div className="flex items-start">
                        {context ? (
                            <Link
                                to={context.url}
                                onClick={(e): void => e.stopPropagation()}
                                className="text-xs text-indigo-600 hover:underline"
                            >
                                {context.label}
                            </Link>
                        ) : (
                            <p className="text-xs text-gray-600">-</p>
                        )}
                    </div>
                    {/* AVATAR + TITLE + REPLIES */}
                    <div className="flex gap-2">
                        {/* AVATAR */}
                        <UserAvatar
                            user={createdBy}
                            size="xs"
                            tooltip={`${createdBy.firstName} ${createdBy.lastName}`}
                            tooltipDirection="left"
                        />
                        {/* TITLE + REPLIES */}
                        <div>
                            <p className="text-sm text-gray-900 line-clamp-2">{title}</p>
                            <p className="text-xs text-gray-500">{replies === 1 ? `1 reply` : `${replies} replies`}</p>
                        </div>
                    </div>
                </div>
                {/* STATUS + DATE */}
                <div className="flex justify-end gap-2">
                    <div className="flex flex-col items-end justify-between">
                        {statusBadge}
                        <TooltipV2 message={formatDateTime(createdAt)} position="left">
                            <p className="text-gray-500 text-xs">{calcRelativeTime(createdAt)}</p>
                        </TooltipV2>
                    </div>

                    <Dropdown disabled={disableAllActions}>
                        {actions
                            .filter((a) => !a.hidden)
                            .map((action) => {
                                return (
                                    <Dropdown.Option
                                        label={action.label}
                                        value={action.value}
                                        key={action.value}
                                        onClick={(_, e): void => {
                                            e?.stopPropagation();
                                            onActionsClick && onActionsClick(action.value);
                                        }}
                                    />
                                );
                            })}
                    </Dropdown>
                </div>
            </div>
        </div>
    );
}
export default ThreadRow;
